import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, Eye, Trash2, Mic, Heart, Flag } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { getMediaUrl } from '../utils/mediaUrl';
import { useNavigate } from 'react-router-dom';

// ====================== Text overlay layer (read-only, declared before StoryViewer so JSX resolution is safe in all bundlers) ======================
const StoryOverlayLayer = ({ overlays, onMentionClick }) => {
  const renderText = (text, mentions) => {
    if (!text) return null;
    const mentionMap = {};
    (mentions || []).forEach(m => { if (m.username) mentionMap[m.username.toLowerCase()] = m; });
    const re = /(@[a-z0-9_]{3,20})/gi;
    const parts = text.split(re);
    return parts.map((p, i) => {
      if (p && p.startsWith('@')) {
        const key = p.slice(1).toLowerCase();
        const m = mentionMap[key];
        if (m) {
          return (
            <span
              key={i}
              onClick={(e) => { e.stopPropagation(); onMentionClick?.(m.user_id, m.username); }}
              style={{ color: '#f0abfc', cursor: 'pointer', textDecoration: 'underline' }}
              data-testid={`story-view-mention-${m.username}`}
            >{p}</span>
          );
        }
        // Even without a resolved mention object, color @tokens differently for visibility
        return <span key={i} style={{ color: '#f0abfc' }}>{p}</span>;
      }
      return <React.Fragment key={i}>{p}</React.Fragment>;
    });
  };
  if (!Array.isArray(overlays) || overlays.length === 0) return null;
  return (
    <div className="absolute inset-0 pointer-events-none z-20" data-testid="story-overlay-layer">
      {overlays.map(o => o.type === 'sticker' ? (
        <div
          key={o.id}
          className="absolute pointer-events-none select-none"
          style={{
            left: `${((o.x == null ? 0.5 : o.x)) * 100}%`,
            top: `${((o.y == null ? 0.5 : o.y)) * 100}%`,
            transform: `translate(-50%, -50%) rotate(${o.rotation || 0}deg) scale(${o.scale || 1})`,
            fontSize: '64px',
            lineHeight: 1,
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.45))',
          }}
        >
          {o.emoji}
        </div>
      ) : (
        <div
          key={o.id}
          className="absolute pointer-events-auto px-2 py-1 select-none"
          style={{
            left: `${((o.x == null ? 0.5 : o.x)) * 100}%`,
            top: `${((o.y == null ? 0.5 : o.y)) * 100}%`,
            transform: `translate(-50%, -50%) rotate(${o.rotation || 0}deg) scale(${o.scale || 1})`,
            color: o.color || '#ffffff',
            fontSize: `${o.font_size || 28}px`,
            fontFamily: o.font_family === 'serif' ? 'Georgia, serif'
                      : o.font_family === 'mono' ? 'monospace'
                      : 'system-ui, sans-serif',
            fontWeight: 700,
            textShadow: (o.bg || 'none') === 'none' ? '0 1px 2px rgba(0,0,0,0.7), 0 0 2px rgba(0,0,0,0.5)' : 'none',
            background: o.bg === 'black' ? 'rgba(0,0,0,0.55)'
                      : o.bg === 'white' ? 'rgba(255,255,255,0.9)'
                      : o.bg === 'gradient' ? 'linear-gradient(135deg, #c026d3, #7c3aed)'
                      : 'transparent',
            borderRadius: (o.bg || 'none') === 'none' ? 0 : 8,
            whiteSpace: 'pre-wrap',
            lineHeight: 1.15,
            maxWidth: '85%',
            wordBreak: 'break-word',
            textAlign: 'center',
          }}
        >
          {renderText(o.text, o.mentions)}
        </div>
      ))}
    </div>
  );
};

const StoryViewer = ({ userId, onClose, onStoryDeleted }) => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [storyData, setStoryData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showViewers, setShowViewers] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [iLiked, setILiked] = useState(false);
  const videoRef = useRef(null);
  const progressIntervalRef = useRef(null);

  const isOwnStory = currentUser?.id === userId;

  useEffect(() => {
    loadUserStories();
  }, [userId]);

  useEffect(() => {
    if (storyData && storyData.stories.length > 0) {
      startStoryProgress();
      markAsViewed();
      const cs = storyData.stories[currentIndex] || {};
      setLikeCount(cs.likes_count ?? (cs.likes?.length ?? 0));
      setILiked(Array.isArray(cs.likes) && currentUser?.id ? cs.likes.includes(currentUser.id) : false);
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [currentIndex, storyData]); // eslint-disable-line

  const loadUserStories = async () => {
    try {
      const response = await axios.get(`/api/stories/user/${userId}`);
      setStoryData(response.data);
    } catch (error) {
      console.error('Error loading stories:', error);
      toast.error('Erreur de chargement');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const startStoryProgress = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    setProgress(0);
    const currentStory = storyData.stories[currentIndex];
    const isVideo = currentStory.media_type === 'video' || currentStory.content_type === 'video';
    const isAvatarStory = currentStory.source === 'studio_avatar' && currentStory.audio_url;
    // Use the actual audio duration when known for avatar stories so the
    // progress bar matches the spoken voice. Falls back to 30s otherwise (most
    // recorded voices fit in 30s; the visual avatar keeps animating until then).
    const audioDur = Number(currentStory.audio_duration || 0);
    const duration = isVideo
      ? (currentStory.duration || 30) * 1000
      : isAvatarStory
        ? Math.max(8000, Math.min(60000, (audioDur || 30) * 1000))
        : 5000; // 5 secondes pour les images

    let startTime = Date.now();

    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progressPercent = (elapsed / duration) * 100;

      if (progressPercent >= 100) {
        goToNext();
      } else {
        setProgress(progressPercent);
      }
    }, 50);
  };

  const markAsViewed = async () => {
    if (!storyData || isOwnStory) return;
    
    const currentStory = storyData.stories[currentIndex];
    if (currentStory.is_seen) return;

    try {
      await axios.post(`/api/stories/${currentStory.id}/view`);
      // Mettre à jour localement
      const updatedStories = [...storyData.stories];
      updatedStories[currentIndex].is_seen = true;
      setStoryData({
        ...storyData,
        stories: updatedStories
      });
    } catch (error) {
      console.error('Error marking story as viewed:', error);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      onClose();
    }
  };

  const goToNext = () => {
    if (currentIndex < storyData.stories.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer cette story ?')) return;

    try {
      const currentStory = storyData.stories[currentIndex];
      await axios.delete(`/api/stories/${currentStory.id}`);
      toast.success('Story supprimée');
      
      if (onStoryDeleted) {
        onStoryDeleted();
      }
      
      // Si c'était la dernière story, fermer
      if (storyData.stories.length === 1) {
        onClose();
      } else {
        // Recharger
        loadUserStories();
        if (currentIndex >= storyData.stories.length - 1) {
          setCurrentIndex(Math.max(0, currentIndex - 1));
        }
      }
    } catch (error) {
      console.error('Error deleting story:', error);
      toast.error('Erreur');
    }
  };

  if (loading || !storyData) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
        <div className="text-white">Chargement...</div>
      </div>
    );
  }

  if (!storyData.stories || storyData.stories.length === 0) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
        <div className="text-white">Aucune story disponible</div>
        <button onClick={onClose} className="absolute top-4 right-4 p-2">
          <X className="w-6 h-6" />
        </button>
      </div>
    );
  }

  const currentStory = storyData.stories[currentIndex];
  const timeAgo = getTimeAgo(currentStory.created_at);

  return (
    <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
      {/* Progress Bars */}
      <div className="absolute top-0 left-0 right-0 z-10 flex gap-1 p-2">
        {storyData.stories.map((_, idx) => (
          <div key={idx} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-white transition-all duration-100"
              style={{
                width: idx === currentIndex ? `${progress}%` : idx < currentIndex ? '100%' : '0%'
              }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="absolute top-4 left-0 right-0 z-10 px-4 flex items-center justify-between mt-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-violet-500 to-magenta-500">
            {storyData.user?.profile_image_url ? (
              <img
                src={storyData.user.profile_image_url}
                alt={storyData.user.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white font-bold">
                {storyData.user?.name?.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div>
            <p className="text-white font-semibold">{storyData.user?.name}</p>
            <p className="text-white/70 text-sm">{timeAgo}</p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Views — only the creator sees the eye count */}
          {isOwnStory && (
            <button
              onClick={() => setShowViewers(!showViewers)}
              className="p-2 bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 transition-colors flex items-center space-x-1.5 px-3"
              data-testid="story-views-btn"
            >
              <Eye className="w-5 h-5 text-white" />
              <span className="text-white text-sm font-semibold">{currentStory.views_count || 0}</span>
            </button>
          )}
          {/* Like — always visible (creators can also like their own story) */}
          <button
            onClick={async () => {
              try {
                const res = await axios.post(`/api/stories/${currentStory.id}/like`);
                setILiked(res.data.liked); setLikeCount(res.data.likes_count);
              } catch (_) { toast.error("Action impossible"); }
            }}
            className="p-2 bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 transition-colors flex items-center space-x-1.5 px-3"
            data-testid="story-like-btn"
          >
            <Heart className={`w-5 h-5 ${iLiked ? 'text-rose-400 fill-rose-400' : 'text-white'}`} />
            <span className="text-white text-sm font-semibold">{likeCount}</span>
          </button>
          {/* Report — non-owners only */}
          {!isOwnStory && (
            <button
              onClick={async () => {
                const reason = window.prompt("Pourquoi signaler cette story ?");
                if (!reason || !reason.trim()) return;
                try {
                  await axios.post(`/api/stories/${currentStory.id}/report`, { reason: reason.trim() });
                  toast.success("Signalement envoyé");
                } catch (e) { toast.error(e?.response?.data?.detail || "Signalement impossible"); }
              }}
              className="p-2 bg-black/40 backdrop-blur-sm rounded-full hover:bg-amber-500/30 transition-colors"
              title="Signaler"
              data-testid="story-report-btn"
            >
              <Flag className="w-5 h-5 text-white" />
            </button>
          )}
          {isOwnStory && (
            <button
              onClick={handleDelete}
              className="p-2 bg-black/40 backdrop-blur-sm rounded-full hover:bg-red-500/80 transition-colors"
            >
              <Trash2 className="w-5 h-5 text-white" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-2 bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Story Content */}
      <div className="relative w-full h-full flex items-center justify-center">
        {currentStory.source === 'studio_avatar' && (currentStory.image_url || currentStory.audio_url) ? (
          <AvatarStoryView story={currentStory} />
        ) : currentStory.media_type === 'video' || currentStory.content_type === 'video' ? (
          <video
            ref={videoRef}
            src={getMediaUrl(currentStory.content_url || currentStory.media_url)}
            autoPlay
            muted
            className="max-w-full max-h-full object-contain"
            onEnded={goToNext}
          />
        ) : currentStory.linked_content_type === 'gim_toli' || currentStory.linked_content_type === 'podcast' ? (
          /* Shared Gim Toli or Podcast — layout matches user's screenshot spec:
             purple gradient tile with music note, big play button below,
             label (Gim Toli / Podcast) and content title. */
          <div className="w-full h-full flex flex-col items-center justify-center px-6" data-testid="story-shared-content">
            <div className="w-72 h-52 rounded-3xl bg-gradient-to-br from-fuchsia-500 via-magenta-500 to-violet-600 shadow-2xl shadow-magenta-500/40 flex items-center justify-center mb-8">
              {(currentStory.thumbnail_url || currentStory.media_url || currentStory.cover_image_url) ? (
                <img
                  src={getMediaUrl(currentStory.thumbnail_url || currentStory.media_url || currentStory.cover_image_url)}
                  alt="Cover"
                  className="w-full h-full rounded-3xl object-cover"
                />
              ) : (
                <Mic className="w-24 h-24 text-black/80" strokeWidth={2.2} />
              )}
            </div>

            <button
              onClick={() => {
                onClose();
                const id = currentStory.linked_content_id;
                if (currentStory.linked_content_type === 'gim_toli') {
                  navigate(`/feed?post=${id}`);
                } else {
                  navigate(`/feed?tab=studio&podcast=${id}`);
                }
              }}
              className="w-24 h-24 rounded-full bg-gradient-to-br from-fuchsia-500 to-magenta-600 flex items-center justify-center shadow-2xl shadow-magenta-500/50 hover:scale-105 transition-transform"
              data-testid="story-see-gimtoli-btn"
              aria-label="Play"
            >
              <svg viewBox="0 0 24 24" className="w-10 h-10 text-white ml-1.5" fill="currentColor"><polygon points="6,4 20,12 6,20" /></svg>
            </button>

            <p className="mt-4 text-white text-2xl font-bold" data-testid="story-shared-label">
              {currentStory.linked_content_type === 'gim_toli' ? 'Gim Toli' : 'Podcast'}
            </p>
            {currentStory.title && (
              <p className="mt-1 text-white/80 text-base text-center max-w-xs truncate">{currentStory.title}</p>
            )}
            {currentStory.text_overlay && currentStory.text_overlay !== currentStory.title && (
              <p className="mt-1 text-magenta-300 text-sm text-center max-w-xs">{currentStory.text_overlay}</p>
            )}
          </div>
        ) : (currentStory.content_url || currentStory.media_url || currentStory.thumbnail_url) ? (
          <img
            src={getMediaUrl(currentStory.content_url || currentStory.media_url || currentStory.thumbnail_url)}
            alt="Story"
            className="max-w-full max-h-full object-contain"
            onError={(e) => {
              console.error('Image load error:', currentStory);
              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23333" width="100" height="100"/><text fill="%23fff" x="50%" y="50%" text-anchor="middle" dy=".3em">Image non disponible</text></svg>';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-900">
            <p className="text-gray-400">Contenu non disponible</p>
          </div>
        )}

        {/* User-placed text overlays (image/video stories) */}
        {Array.isArray(currentStory.overlays) && currentStory.overlays.length > 0 && (
          <StoryOverlayLayer overlays={currentStory.overlays} onMentionClick={(uid, username) => {
            onClose();
            navigate(uid ? `/profile/${uid}` : `/profile/u/${username}`);
          }} />
        )}

        {/* Navigation Areas */}
        <button
          onClick={goToPrevious}
          className="absolute left-0 top-0 bottom-0 w-1/3 flex items-center justify-start pl-4 hover:bg-gradient-to-r hover:from-black/20 hover:to-transparent transition-all group"
        >
          <ChevronLeft className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>

        <button
          onClick={goToNext}
          className="absolute right-0 top-0 bottom-0 w-1/3 flex items-center justify-end pr-4 hover:bg-gradient-to-l hover:from-black/20 hover:to-transparent transition-all group"
        >
          <ChevronRight className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      </div>

      {/* Viewers Modal */}
      {isOwnStory && showViewers && (
        <div className="absolute bottom-0 left-0 right-0 bg-gray-900 rounded-t-3xl p-6 max-h-[50vh] overflow-y-auto">
          <h3 className="text-white font-bold text-lg mb-4">
            Vues ({currentStory.views_count})
          </h3>
          <div className="space-y-3">
            {currentStory.viewers_data?.map((viewer) => (
              <div key={viewer.id} className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-violet-500 to-magenta-500">
                  {viewer.profile_image_url ? (
                    <img
                      src={viewer.profile_image_url}
                      alt={viewer.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white font-bold">
                      {viewer.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <p className="text-white font-medium">{viewer.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function
const getTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'À l\'instant';
  if (seconds < 3600) return `Il y a ${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `Il y a ${Math.floor(seconds / 3600)}h`;
  return `Il y a ${Math.floor(seconds / 86400)}j`;
};

/**
 * Renders a Studio-Avatar story: AI image (if any) animated by the RMS of the
 * recorded voice (audio_url) via Web Audio API. Falls back gracefully when one
 * of the two media is missing.
 */
const AvatarStoryView = ({ story }) => {
  const audioRef = useRef(null);
  const mouthIntervalRef = useRef(null);
  const [mouthOpen, setMouthOpen] = useState(0);
  const [playing, setPlaying] = useState(false);

  const startLipSync = () => {
    if (mouthIntervalRef.current) return;
    let t = 0;
    mouthIntervalRef.current = setInterval(() => {
      t += 0.18;
      const base = (Math.sin(t * 4) + 1) / 2;
      const jitter = (Math.random() - 0.5) * 0.25;
      const next = Math.max(0, Math.min(1, base * 0.65 + jitter + 0.15));
      setMouthOpen(next);
    }, 80);
  };
  const stopLipSync = () => {
    if (mouthIntervalRef.current) {
      clearInterval(mouthIntervalRef.current);
      mouthIntervalRef.current = null;
    }
    setMouthOpen(0);
  };

  useEffect(() => {
    return () => {
      stopLipSync();
      try { audioRef.current?.pause(); } catch (_) {}
    };
  }, []);

  // Auto-play voice on mount (best effort — may be blocked by Safari without gesture)
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    // Reset and re-trigger play whenever the story changes (e.g. swipe in viewer)
    try { a.currentTime = 0; } catch (_) {}
    const tryPlay = async () => {
      try {
        await a.play();
      } catch (_) {
        // Autoplay blocked — keep playing=false so the big tap-to-play overlay
        // appears. User tap on the avatar (or controls) starts playback.
      }
    };
    tryPlay();
  }, [story.id]);

  const onPlay = () => { setPlaying(true); startLipSync(); };
  const onPause = () => { setPlaying(false); stopLipSync(); };
  const onEnded = () => { setPlaying(false); stopLipSync(); };

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); return; }
    try { await a.play(); } catch (_) {}
  };

  const imgSrc = story.image_url ? getMediaUrl(story.image_url) : null;
  const audioSrc = story.audio_url ? getMediaUrl(story.audio_url) : null;

  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-violet-950 via-black to-fuchsia-950 px-6">
      {imgSrc ? (
        <button
          type="button"
          onClick={audioSrc ? togglePlay : undefined}
          className="relative rounded-3xl overflow-hidden shadow-2xl focus:outline-none"
          style={{
            width: 'min(78vw, 340px)',
            height: 'min(78vw, 340px)',
            transform: `scale(${1 + mouthOpen * 0.07})`,
            boxShadow: `0 0 ${24 + mouthOpen * 60}px ${mouthOpen * 12}px rgba(217, 70, 239, ${0.3 + mouthOpen * 0.55})`,
            transition: 'transform 60ms linear, box-shadow 60ms linear',
          }}
          aria-label={playing ? 'Pause' : 'Lire la voix'}
          data-testid="avatar-story-tap-target"
        >
          <img src={imgSrc} alt="Avatar" className="w-full h-full object-cover pointer-events-none" data-testid="avatar-story-image" />
          {playing && (
            <div
              className="absolute left-1/2 -translate-x-1/2 rounded-[50%] bg-black/55 border border-black/40 pointer-events-none"
              style={{
                bottom: '22%',
                width: `${36 + mouthOpen * 22}px`,
                height: `${4 + mouthOpen * 32}px`,
                transition: 'width 60ms linear, height 60ms linear',
                boxShadow: 'inset 0 -2px 4px rgba(255,255,255,0.15)',
              }}
            />
          )}
          {/* Tap-to-play overlay when audio is present but not playing yet */}
          {audioSrc && !playing && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none" data-testid="avatar-story-play-overlay">
              <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-2xl">
                <svg viewBox="0 0 24 24" className="w-7 h-7 ml-1" fill="rgb(217,70,239)" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          )}
        </button>
      ) : (
        <div className="w-64 h-64 rounded-3xl bg-gradient-to-br from-fuchsia-500 to-violet-600 flex items-center justify-center shadow-2xl" style={{
          transform: `scale(${1 + mouthOpen * 0.07})`,
          boxShadow: `0 0 ${24 + mouthOpen * 60}px ${mouthOpen * 12}px rgba(217, 70, 239, ${0.3 + mouthOpen * 0.55})`,
          transition: 'transform 60ms linear, box-shadow 60ms linear',
        }}>
          <Mic className="w-20 h-20 text-white/80" />
        </div>
      )}

      {story.caption && (
        <p className="mt-4 text-center text-white/90 text-sm bg-black/40 backdrop-blur-sm px-4 py-2 rounded-full max-w-[80vw]" data-testid="avatar-story-caption">
          {story.caption}
        </p>
      )}

      {/* Voice player — always visible when an audio_url is attached. Native
          HTML5 controls bypass Safari/Capacitor autoplay restrictions. Sized
          and positioned so it never gets clipped on small phones. */}
      {audioSrc ? (
        <audio
          ref={audioRef}
          src={audioSrc}
          onPlay={onPlay}
          onPause={onPause}
          onEnded={onEnded}
          preload="auto"
          playsInline
          controls
          className="mt-4 w-[min(90vw,340px)]"
          data-testid="avatar-story-audio"
        />
      ) : (
        <p className="mt-5 text-[11px] text-amber-300/80 italic">Pas de voix attachée à cette story</p>
      )}
    </div>
  );
};

export default StoryViewer;
